import { Component, DestroyRef, inject, OnInit, ViewChild, TemplateRef, ChangeDetectionStrategy } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Meta } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { firstValueFrom } from "rxjs";

import {
  BaseComponent,
  CommunityService,
  EventsService,
  HelperService,
  OrderService,
  ProductService,
  StorageKeys,
  StorageService,
  SupportService,
  User,
  UserService,
  WindowService,
  WorkspaceService,
  environment,
} from "@mypxplat/xplat/core";
import { AppService, AvoService, FirebaseService, ReftagService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { AuthenticateFenderUserComponent } from "../modals/authenticate-fender-user/authenticate-fender-user.component";
import { AuthenticatePresonusUserComponent } from "../modals/authenticate-presonus-user/authenticate-presonus-user.component";
import { ChangePasswordComponent } from "../modals/change-password/change-password.component";
import { ForgotpasswordComponent } from "../modals/forgotpassword/forgotpassword.component";
import { RegisterSuccessSplashComponent } from "../modals/register-success-splash/register-success-splash.component";
import { SelectCountryComponent } from "../modals/select-country/select-country.component";
import { TranslateService } from "@ngx-translate/core";
import {
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  sendUserAttributeVerificationCode,
  signIn,
  signOut,
  signUp,
  autoSignIn,
  signInWithRedirect,
  updateUserAttributes,
  confirmSignIn,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { NgxSpinnerService } from "ngx-spinner";
import { filter, take } from "rxjs";
import { Amplify } from "aws-amplify";
import { CookieService } from "ngx-cookie-service";
import { ReCaptchaV3Service } from "ng-recaptcha";

/**
 * How long a stashed social-sign-in destination stays good. Long enough to cover a provider
 * consent screen and a password manager; short enough that an abandoned attempt can't
 * redirect someone on an unrelated sign-in later.
 */
const SOCIAL_RETURN_TO_TTL_MS = 15 * 60 * 1000;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-connect-auth",
  templateUrl: "connect-auth.component.html",
  standalone: false,
})
export class ConnectAuthComponent extends BaseComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  fbService = inject(FirebaseService);
  db = inject(AngularFirestore);
  destroyRef = inject(DestroyRef);

  public loggedInUserId: any;
  public email: string = "";
  public first_name: string = "";
  public last_name: string = "";
  public password: string = "";
  public password2: string = "";
  public wantsEmail: boolean;
  public termsCheck: boolean = true;
  public loading: boolean;
  public loginError: string;
  public loggedInUser: User;
  public authSections: Array<any>;
  public displayedAuthSection = "Sign in";
  public passwordPasses: boolean = false;
  public emailPasses: boolean = false;
  public ssoType: string;
  public isEmployeeEmail: boolean = false;
  public ssoDestinations = {
    shop: {
      title: "Shop",
      src: this.env.shopUrl + "auth/login?jwt=",
      type: "queryParam",
      message: "Login or sign up below to complete your purchase.",
      extraParams: {
        groups: true,
      },
    },
    uux: {
      title: "UUX",
      src: this.env.uuxUrl + "auth/login?jwt=",
      type: "queryParam",
      message: "Login below to continue to UUX.",
      extraParams: {},
    },
    zendesk: {
      title: "Zendesk",
      src: "https://support.presonus.com/access/jwt?jwt=",
      type: "queryParam",
      message: "Login below to continue to Zendesk.",
      extraParams: {},
    },
    forum: {
      title: "Forums",
      src: "https://forums.presonus.com/ucp.php?mode=login&returnAfter=aW5kZXgucGhwP3NpZD03ZWFiYzhjYmQ0MDc2ZjI4MWQzNjA4NjQ3MWYzZTI0Zg==&jwt=",
      type: "queryParam",
      message: "Login or sign up below to continue to PreSonus Forums.",
      extraParams: {
        groups: true,
        permissions: true,
      },
    },
    media: {
      title: "Media Vault",
      src: "https://media.presonus.com/auth/login?jwt=",
      type: "queryParam",
      message: "Login below to continue to PreSonus Media Vault.",
      extraParams: {
        groups: true,
      },
    },
    q2a: {
      title: "Answers",
      src: "https://answers.presonus.com/jwt.php?jwt=",
      type: "queryParam",
      message: "Login or sign up below to continue to PreSonus Answers.",
      extraParams: {
        groups: true,
        products: true,
        groupDescriptions: true,
      },
    },

    admin: {
      title: "PreSonus Admin",
      src: environment.adminUrl,
      type: "auth",
      message: "Sign into your admin account to continue.",
      extraParams: {},
      useReferrer: true,
    },
    fender: {
      title: "Fender",
      src: environment.fenderUrl,
      type: "auth",
      message: "",
      extraParams: {},
    },
    sleekplan: {
      title: "PreSonus Feedback",
      src: `${environment.sleekplanUrl}/?sso=`,
      type: "queryParam",
      queryParamName: "sso",
      message: "Sign into account to continue to our Feedback portal.",
      extraParams: {},
      useReferrer: true,
    },
    shopify: {
      title: "PreSonus Shop",
      type: "queryParam",
      returnQueryParam: "returnUrl",
      message: "Sign into your account to continue.",
      extraParams: {},
    },
    fender_shopify: {
      title: "Fender.com",
      type: "queryParam",
      returnQueryParam: "returnUrl",
      message: "Sign into your account to continue.",
      extraParams: {},
    },
    desktop: {
      title: "Desktop",
      type: "queryParam",
      message: "Sign into your account to continue.",
      extraParams: {},
      cognitoTokens: true,
    },
    mobile: {
      title: "Mobile App",
      message: "Sign into your account to continue.",
      extraParams: {},
      cognitoTokens: true,
    },
  };

  public accessToken: string;
  public idToken: string;
  public refreshToken: string;
  public needsVerification: boolean = false;
  public requiresNewPassword: boolean = false;
  public newPassword: string = "";
  public confirmNewPassword: string = "";
  public newPasswordPasses: boolean = false;
  public cognitoUser: any;
  public verificationCode: string;
  public codeSent: boolean = false;
  public passwordVisible: boolean = false;
  public returnto: string = "/home";
  public isDemoReturnTo: boolean = false;
  public returnToUrlParams: any = {};
  public locationResult: any;
  public countriesMap: any;
  public countries: Array<any>;
  public selectedCountry: any = {
    name: "United States",
    code: "US",
  };
  public shopifyStoreID: string;
  public captcha: any;
  public source: any;
  public showV2Captcha: boolean = false;
  public fenderProductCallout: any;
  public loadingCredentials: boolean = false;
  public queryParams: any;
  public shouldShowS17Demo: boolean = false;
  public productSplash: any;
  private currentPool: "primary" | "secondary" = "primary";
  public authenticatingPresonusAccount: boolean = false;
  public presonusIdToken: string;

  // Guards to prevent concurrent event processing and loops
  private isProcessingAuthEvent: boolean = false;
  private tokenRefreshFailureCount: number = 0;
  private readonly MAX_TOKEN_REFRESH_RETRIES = 3;
  private sessionSetupInProgress: boolean = false;
  private fetchingAuthSession: boolean = false;
  private authSessionInFlight: Promise<any> | null = null;
  private authSessionInFlightForceRefresh: boolean = false;

  public showMultiSelectOptions: boolean = false;
  public shouldShowPresonusFenderMessage: boolean = false;

  public marketingCallout: any;
  // public marketingCallout: any = {
  //   title: "Fender Studio Pro Now 30% Off",
  //   message: "Offer valid through Sept 2, 2026.",
  //   buttonText: "Buy Now",
  //   buttonLink: "https://my.fender.com/onboarding",
  // };

  constructor(
    userService: UserService,
    public appService: AppService,
    public helperService: HelperService,
    private _translate: TranslateService,
    public router: Router,
    private _spin: NgxSpinnerService,
    public productService: ProductService,
    public eventsService: EventsService,
    public orderService: OrderService,
    public supportService: SupportService,
    public communityService: WebCommunityService,
    public workspaceService: WorkspaceService,
    public fbAuth: AngularFireAuth,
    public avoService: AvoService,
    private _win: WindowService,
    public reftagService: ReftagService,
    public metaService: Meta,
    public storageService: StorageService,
    public cookieService: CookieService,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {
    super(userService);
  }

  private setReturnto(value: string) {
    this.returnto = value;
    this.isDemoReturnTo = !!value && value.indexOf("studioprodemo") > -1;
  }

  ngOnInit() {
    this.handleRedirectedFromPresonus();

    this.initSkills();
    this.initializeBasicSettings();
    this.setupPageMetadata();
    this.initializeAuthenticationSections();
    this.setupCountriesAndLocation();
    this.handleRouteBasedInitialization();
    this.setupAuthenticationEventListeners();
    this.appService.hasInittedApp = false;

    const shouldOpenForgotPasswordModal =
      this.activatedRoute.snapshot.queryParams["forgotPassword"] === "true" ||
      (this.router?.routerState?.snapshot?.url?.indexOf("sign-in-help") ?? -1) > -1 ||
      (this.router?.routerState?.snapshot?.url?.indexOf("forgot-password") ?? -1) > -1 ||
      (this.router?.routerState?.snapshot?.url?.indexOf("forgot_password") ?? -1) > -1 ||
      (this.router?.routerState?.snapshot?.url?.indexOf("auth/forgot_password") ?? -1) > -1;
    if (shouldOpenForgotPasswordModal) {
      this._win.setTimeout(() => {
        this.startForgotPassword();
        const qp = { ...this.activatedRoute.snapshot.queryParams };
        delete qp.forgotPassword;
        this.router.navigate(["/login"], { queryParams: qp, replaceUrl: true });
      }, 0);
    }
  }

  /**
   * Checks for redirectedFromPresonus parameter in query params or returnto URL,
   * sets shouldShowPresonusFenderMessage if found, and cleans it from the URL
   */
  private handleRedirectedFromPresonus(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const returntoParam = queryParams.returnto;
    const hasInQueryParams = !!queryParams.redirectedFromPresonus;
    const decodedReturnto = returntoParam ? decodeURIComponent(returntoParam) : null;
    const hasInReturnto = decodedReturnto && decodedReturnto.indexOf("redirectedFromPresonus") > -1;
    const hasRedirectedFromPresonus = hasInQueryParams || hasInReturnto;

    if (hasRedirectedFromPresonus) {
      this.shouldShowPresonusFenderMessage = true;
      let allQueryParams = { ...queryParams };

      // Remove from direct query params
      delete allQueryParams.redirectedFromPresonus;

      // If it's in returnto, clean it from there
      let cleanedReturntoDecoded: string | null = null;
      if (hasInReturnto && decodedReturnto) {
        try {
          // Parse the returnto URL to separate path and query string
          const urlParts = decodedReturnto.split("?");
          const path = urlParts[0];
          const queryString = urlParts[1];

          if (queryString) {
            // Parse query string and remove redirectedFromPresonus
            const params = new URLSearchParams(queryString);
            params.delete("redirectedFromPresonus");

            // Reconstruct the returnto URL
            const newQueryString = params.toString();
            cleanedReturntoDecoded = newQueryString ? `${path}?${newQueryString}` : path;

            // Re-encode and update
            allQueryParams.returnto = encodeURIComponent(cleanedReturntoDecoded);
          } else {
            // No query string, just use the path as-is
            cleanedReturntoDecoded = path;
            allQueryParams.returnto = encodeURIComponent(path);
          }
        } catch (e) {
          // If parsing fails, just remove redirectedFromPresonus from the string and re-encode
          // Handle cases where it's at the start (?redirectedFromPresonus=...), middle (&redirectedFromPresonus=...), or end
          let cleaned = decodedReturnto.replace(/[?&]redirectedFromPresonus=[^&]*/g, "");
          // Clean up any ?& sequences (when param was first) or trailing ?/&
          cleaned = cleaned.replace(/\?&/g, "?").replace(/\?$/, "").replace(/&$/, "");
          cleanedReturntoDecoded = cleaned;
          allQueryParams.returnto = encodeURIComponent(cleaned);
        }
      }

      // Update this.returnto with the cleaned value so setupReturnToUrl() will use it
      // If we cleaned it from returnto, use the cleaned version; otherwise use the original decoded returnto if it exists
      if (cleanedReturntoDecoded) {
        this.setReturnto(cleanedReturntoDecoded);
      } else if (returntoParam) {
        // Not in returnto, but returnto exists - use it as-is (decoded)
        this.setReturnto(decodedReturnto);
      }

      this.router.navigate([], { queryParams: allQueryParams });
    }
  }

  // #region Core Authentication Utilities

  /**
   * Debug logger for authentication flow
   * Helps track the complex authentication state changes
   */
  private logAuthState(action: string, details?: any): void {
    if (!environment.production) {
      const state = {
        action,
        timestamp: new Date().toISOString(),
        isProcessingAuthEvent: this.isProcessingAuthEvent,
        sessionSetupInProgress: this.sessionSetupInProgress,
        fetchingAuthSession: this.fetchingAuthSession,
        tokenRefreshFailureCount: this.tokenRefreshFailureCount,
        loading: this.loading,
        ...(details && { details }),
      };
      console.log(`🔐 Auth State [${action}]:`, state);
    }
  }

  /**
   * Managed method to fetch auth session with guard against concurrent calls
   * This prevents race conditions and overlapping token refreshes
   */
  private async getManagedAuthSession(forceRefresh: boolean = false, context: string = "unknown"): Promise<any> {
    // Log the request for debugging
    this.logAuthState(`fetchAuthSession [${context}]`, { forceRefresh });

    // Single-flight: share the same in-progress fetch across all callers.
    // This matters on OAuth redirects where BOTH:
    // - ngOnInit() calls checkForLoggedInUser(forceRefresh=true), and
    // - Amplify Hub emits signedIn which also fetches the session
    // can happen nearly simultaneously.
    if (this.authSessionInFlight) {
      // If caller doesn't need a refresh, or the in-flight call is already a refresh, join it.
      if (!forceRefresh || this.authSessionInFlightForceRefresh) {
        if (!environment.production) console.log("⏸️ Auth session fetch already in progress, joining...");
        return await this.authSessionInFlight;
      }

      // Caller needs force refresh but in-flight is not force-refresh; wait, then retry with refresh.
      if (!environment.production) console.log("⏭️ Waiting for in-flight auth session fetch before force refresh...");
      await this.authSessionInFlight;
      // Note: after awaiting, another caller may have started a new in-flight request; check again.
      if (this.authSessionInFlight) {
        if (!environment.production) console.log("⏸️ Another auth session fetch started, joining...");
        return await this.authSessionInFlight;
      }
    }

    this.fetchingAuthSession = true;
    this.authSessionInFlightForceRefresh = forceRefresh;
    this.authSessionInFlight = (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh });
        if (!environment.production) {
          console.log(`✅ Auth session fetched successfully (context: ${context})`);
        }
        return session;
      } catch (error) {
        if (!environment.production) {
          console.error(`❌ Failed to fetch auth session (context: ${context}):`, error);
        }
        throw error;
      } finally {
        this.fetchingAuthSession = false;
        this.authSessionInFlight = null;
        this.authSessionInFlightForceRefresh = false;
      }
    })();

    return await this.authSessionInFlight;
  }

  // #endregion

  // #region Initialization Methods

  /**
   * Initialize basic component settings and UI
   */
  private initializeBasicSettings(): void {
    this.shouldShowS17Demo = environment.features.s17_demo;
    this.queryParams = this.activatedRoute.snapshot.queryParams;

    // Initialize video autoplay
    this._win.setTimeout(() => {
      const myVideo = document.getElementsByTagName("video")[0];
      if (myVideo) {
        myVideo.muted = true;
        myVideo.play().catch((error) => console.error("Autoplay was prevented: ", error));
      }
    }, 800);

    // Setup authentication pools
    this._win.setTimeout(() => {
      this.switchPools("fender");
    }, 100);

    // UI setup
    this.fenderProductCallout = this.router.routerState.snapshot.url.indexOf("mustangmicro") > -1;
    const body = document.getElementsByTagName("body")[0];
    body.classList.add("full-page");

    this.appService.initTheme("auto");
  }

  /**
   * Setup page title and meta tags for SEO
   */
  public signupInterests: Array<any> = [];
  public selectedSignupInterestsArray: Array<any> = [];
  public selectedSignupInterests: any = {};
  private initSkills(): void {
    this.communityService.getAvailableSkills().subscribe((result) => {
      this.signupInterests = result.filter((item) => item.string_id == "music_production" || item.string_id == "guitar" || item.string_id == "acoustic_guitar" || item.string_id == "bass_guitar");
    });
  }

  selectSignupInterest(interest: any): void {
    this.selectedSignupInterests[interest.string_id] = !this.selectedSignupInterests[interest.string_id];
    if (this.selectedSignupInterests[interest.string_id]) {
      this.selectedSignupInterestsArray.push(interest);
    } else {
      this.selectedSignupInterestsArray = this.selectedSignupInterestsArray.filter((item) => item.string_id !== interest.string_id);
    }
  }

  /**
   * Setup page title and meta tags for SEO
   */
  private setupPageMetadata(): void {
    this.appService.title = "MyFender Account Login – Register Products, " + this.env.studioOnePlusBrandName + ", 30-Day Free Demo";

    const metaTags = this.buildMetaTags();
    this.metaService.addTags(metaTags);
  }

  displayAuthSection(section: string) {
    this.displayedAuthSection = section;
    this.loginError = null;
  }

  /**
   * Build meta tags array for SEO
   */
  private buildMetaTags(): any[] {
    const baseDescription =
      "Log in or create a MyFender account to manage or register Fender & PreSonus hardware and software, download a free 30-day demo of Fender Studio Pro, sign up for " +
      this.env.studioOnePlusBrandName +
      ", purchase software, and more.";
    const baseTitle = "MyFender Account Login – Register Products, " + this.env.studioOnePlusBrandName + ", 30-Day Free Demo";
    const baseImage = "http://pae-web.presonusmusic.com/uploads/products/mediabars/english/images/so6-webpage-hero.jpg";

    return [
      { name: "Description", content: baseDescription },
      { itemprop: "name", content: baseTitle },
      { itemprop: "Description", content: baseDescription },
      { itemprop: "image", content: baseImage },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Fender" },
      { name: "twitter:title", content: baseTitle },
      { name: "twitter:description", content: baseDescription },
      { name: "twitter:creator", content: "@PreSonus" },
      { name: "twitter:image:src", content: baseImage },
      { property: "og:url", content: "https://my.fender.com/" },
      { property: "og:title", content: baseTitle },
      { property: "og:description", content: baseDescription },
      { property: "og:site_name", content: baseTitle },
      { property: "og:image", content: baseImage },
      { property: "fb:app_id", content: "243955558952702" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      ...this.buildKeywordMetaTags(),
    ];
  }

  /**
   * Build keyword meta tags
   */
  private buildKeywordMetaTags(): any[] {
    const keywords = [
      "my.fender",
      "Fender register",
      "Fender product registration",
      "MyFender",
      "My ",
      "MyFender login",
      "My fender login",
      "my.fender login",
      "Fender Studio Pro login",
      "Fender Studio Pro+ login",
      "Fender login",
      "Log into my fender",
      "Log into myfender",
      "Fender Studio Pro",
    ];

    return keywords.map((keyword) => ({ name: keyword, content: keyword }));
  }

  /**
   * Initialize authentication sections (Login/Sign Up tabs)
   */
  private initializeAuthenticationSections(): void {
    this.authSections = [
      {
        name: "Sign in",
        translationKey: "login.sign_in",
        selected: true,
      },
      {
        name: "Sign Up",
        translationKey: "onboarding.signup",
      },
    ];
  }

  /**
   * Setup countries list and user location detection
   */
  private setupCountriesAndLocation(): void {
    this.userService.listCountries().subscribe({
      next: (result) => {
        this.processCountriesData(result);
        this.detectUserLocation();
      },
    });
  }

  /**
   * Process countries data from API response
   */
  private processCountriesData(result: any): void {
    this.countriesMap = {};
    const countries = [];

    for (let i in result) {
      if (i !== "") {
        this.countriesMap[result[i]] = i;
        countries.push(result[i]);
      }
    }

    this.countries = countries;
  }

  /**
   * Detect user's location and set default country
   */
  private detectUserLocation(): void {
    this.userService.getCountry().subscribe((locationResult: any) => {
      if (locationResult) {
        this.locationResult = locationResult;

        if (this.locationResult.country_code && this.locationResult.country_code !== "US") {
          this.setSelectedCountryFromLocation();
        }
      }
    });
  }

  /**
   * Set selected country based on detected location
   */
  private setSelectedCountryFromLocation(): void {
    for (const countryName in this.countriesMap) {
      if (this.countriesMap[countryName] === this.locationResult.country_code) {
        this.selectedCountry = {
          name: countryName,
          code: this.countriesMap[countryName],
        };
        break;
      }
    }
  }

  /**
   * Handle route-based initialization logic
   */
  private handleRouteBasedInitialization(): void {
    if (this.queryParams.logout || this.router.url.indexOf("/logout") > -1) {
      this.handleLogoutFlow();
      return;
    }

    this.handleNormalFlow();
  }

  /**
   * Handle logout flow from query parameters
   */
  private handleLogoutFlow(): void {
    this.ssoType = this.queryParams.sso ? this.queryParams.sso.toLowerCase() : "";

    this.router.navigate([], {
      queryParams: { logout: null },
      queryParamsHandling: "merge",
    });

    if (this.avoService.avoInitted) {
      this.logout();
      if (this.queryParams.returnUrl) {
        this._spin.show();
        window.location = this.queryParams.returnUrl;
      }
    } else {
      this.avoService.avoInitted$
        .pipe(
          filter((initted) => !!initted),
          take(1)
        )
        .subscribe(() => {
          this.logout();
          if (this.queryParams.returnUrl) {
            this._spin.show();
            window.location = this.queryParams.returnUrl;
          }
        });
    }
  }

  /**
   * Handle normal initialization flow
   */
  private handleNormalFlow(): void {
    this.loadingCredentials = true;
    this.appService.setTokensFromCookies();
    this.appService.signInFromSavedTokens(undefined, undefined, undefined);

    this.setupReturnToUrl();
    this.setupAuthSectionFromRoute();
    this.checkForProductSplash();
    this.setupSSOParameters();
    this.applySSOSpecificStyling();
    this.setupReturnToUrlFromRoute();

    const cognitoClientId = this.determineCognitoClientId();
    this.checkForLoggedInUser(cognitoClientId);
  }

  /**
   * Setup return URL from query parameters
   */
  private setupReturnToUrl(): void {
    // Don't override returnto if it's already been set to a non-default value
    // (e.g., by handleRedirectedFromPresonus() which cleans the returnto)
    const returntoAlreadySet = this.returnto && this.returnto !== "/home";
    if (!returntoAlreadySet && (this.queryParams.returnto || this.queryParams.ref)) {
      this.setReturnto(decodeURIComponent(this.queryParams.returnto || this.queryParams.ref));
    }

    // Some callers encode ref= inside the source param instead of as a standalone query param
    // e.g. ?source=play_web%3Fref%3Dhttps://... — extract ref from source as returnto fallback
    if (!returntoAlreadySet && this.returnto === "/home" && this.queryParams.source) {
      const decodedSource = decodeURIComponent(this.queryParams.source);
      const refMatch = decodedSource.match(/[?&]ref=(.+)/);
      if (refMatch) {
        this.setReturnto(decodeURIComponent(refMatch[1]));
      }
    }

    if (this.returnto === "/login" || this.returnto === "/createaccount") {
      this.setReturnto("/home");
    }
  }

  /**
   * Setup authentication section based on route
   */
  private setupAuthSectionFromRoute(): void {
    const url = this.router.routerState.snapshot.url;
    const signUpRoutes = ["join", "createaccount", "signup", "register"];

    if (signUpRoutes.some((route) => url.indexOf(route) > -1)) {
      this.displayedAuthSection = "Sign Up";
      this.authSections[0].selected = false;
      this.authSections[1].selected = true;
    }
  }

  /**
   * Check for product splash parameters
   */
  private checkForProductSplash(): void {
    const url = this.router.routerState.snapshot.url;
    const splashParam = this.activatedRoute.snapshot.params["splash"];

    if (url.indexOf("product") > -1 && splashParam) {
      this.fbService.checkForSplash(undefined, splashParam).then((result: any) => {
        if (result) {
          this.productSplash = result;
          this.setReturnto(`/home?register=${splashParam}`);
        }
      });
    }
  }

  /**
   * Setup SSO parameters from stored or query params
   */
  private setupSSOParameters(): void {
    const storedSsoParams = this.storageService.getItem(StorageKeys.SSO_PARAMS);
    const isDesktopLogin = this.isDesktopLogin();

    if (!this.queryParams.sso && !isDesktopLogin && storedSsoParams) {
      this.queryParams = storedSsoParams;
      this.storageService.removeItem(StorageKeys.SSO_PARAMS);
    }

    this.determineSSOType();

    if (this.queryParams.sso || this.queryParams.client || this.queryParams.client_id) {
      this.storageService.setItem(StorageKeys.SSO_PARAMS, this.queryParams);
    }
  }

  /**
   * Check if this is a desktop application login
   */
  private isDesktopLogin(): boolean {
    const clientId = this.queryParams.client_id;
    let isDesktopLogin = false;
    if (clientId && (clientId.indexOf("STUDIO") > -1 || clientId.indexOf("NOTION") > -1 || clientId.indexOf("UNIVERSAL") > -1)) {
      isDesktopLogin = true;
    }
    return isDesktopLogin;
  }

  /**
   * Determine SSO type from various route conditions
   */
  private determineSSOType(): void {
    if (this.queryParams.sso) {
      this.ssoType = this.queryParams.sso.toLowerCase();
      if (this.queryParams.returnUrl && (this.queryParams.returnUrl.indexOf("fender.com") > -1 || this.queryParams.returnUrl.indexOf("fendergarage.com") > -1)) {
        this.ssoType = "fender_shopify";
      }
    } else if (this.queryParams.client || this.queryParams.client_id) {
      this.ssoType = "desktop";
    } else {
      this.ssoType = this.detectSSOTypeFromRoute();
    }
  }

  /**
   * Detect SSO type from current route
   */
  private detectSSOTypeFromRoute(): string {
    const url = this.router.routerState.snapshot.url;
    if (url.indexOf("sso") > -1) {
      if (url.indexOf("zendesk") > -1) return "zendesk";
      if (url.indexOf("media") > -1) return "media";
    }

    if (url.indexOf("sso=mobile") > -1) return "mobile";

    // if (url.indexOf("reftag") === -1 && url.indexOf("ref") > -1 && (url.indexOf("fendergarage") > -1 || url.indexOf("presonus.com") > -1)) {
    //   return "sfcc";
    // }

    return null;
  }

  /**
   * Apply SSO-specific styling
   */
  private applySSOSpecificStyling(): void {
    if (this.ssoType === "fender") {
      const body = document.getElementsByTagName("body")[0];
      body.classList.add("fender");
    }
  }

  /**
   * Setup return URL based on route conditions
   */
  private setupReturnToUrlFromRoute(): void {
    const url = this.router.routerState.snapshot.url;
    if (url.indexOf("mustangmicro") > -1 && !this.ssoType) {
      this.setReturnto("/home?register=mustangmicro");
    } else if (this.returnto) {
      this.returnToUrlParams = this.findURLParameters(this.returnto);
    }
  }

  /**
   * Determine the correct Cognito client ID to use
   */
  private determineCognitoClientId(forceBrand?: string): string {
    // IMPORTANT:
    // - If we're switching pools explicitly (forceBrand), never allow query-param client IDs
    //   to "fall back" across brands. That can result in configuring the Fender pool with a
    //   PreSonus app client ID (or vice versa), and can also be influenced by stale SSO params
    //   in storage.
    const targetBrand: "fender" | "presonus" = (forceBrand as any) === "presonus" ? "presonus" : "fender";
    let cognitoClientId = targetBrand === "fender" ? this.env.fenderCognito.clientId : this.env.cognito.clientId;

    const clientParam = this.queryParams.client || this.queryParams.client_id;
    if (clientParam) {
      if (targetBrand === "fender") {
        if (this.env.fenderCognito.appClients && this.env.fenderCognito.appClients[clientParam]) {
          cognitoClientId = this.env.fenderCognito.appClients[clientParam];
        }
      } else {
        if (this.env.cognito.appClients && this.env.cognito.appClients[clientParam]) {
          cognitoClientId = this.env.cognito.appClients[clientParam];
        }
      }
    }
    // we need to use the web client for studio one because the backend uses the web client id for validation
    if (targetBrand === "presonus" && this.queryParams.client_id && this.queryParams.client_id == "STUDIO_ONE") {
      cognitoClientId = this.env.cognito.appClients["WEB"];
    }
    return cognitoClientId;
  }

  /**
   * Setup authentication event listeners
   */
  private setupAuthenticationEventListeners(): void {
    Hub.listen("auth", (result) => {
      this.handleAuthEvent(result.payload.event);
    });
  }

  /**
   * Handle authentication events from AWS Amplify Hub
   * Protected against concurrent processing to prevent loops
   */
  private handleAuthEvent(event: string): void {
    this.logAuthState(`Hub event received: ${event}`);

    // Guard: Prevent concurrent event processing
    if (this.isProcessingAuthEvent) {
      if (!environment.production) console.log(`⏸️ Already processing an auth event, skipping ${event}`);
      this.logAuthState(`Event skipped due to concurrent processing`, { skippedEvent: event });
      return;
    }

    switch (event) {
      case "signedIn":
        if (!environment.production) console.log("⏬ Handling signed in event.");
        this.handleSignedInEvent();
        break;

      case "signedOut":
        if (!environment.production) console.log("⛔ Handling signed out event.");
        this.handleSignedOutEvent();
        break;

      case "tokenRefresh":
        if (!environment.production) console.log("♻️ Token refresh event triggered (no action needed).");
        // Intentionally not handling - AWS Amplify handles this automatically
        break;

      case "tokenRefresh_failure":
        if (!environment.production) console.log("❌ Handling token refresh failure event.");
        this.handleTokenRefreshFailureEvent();
        break;
    }
  }

  // #endregion

  // #region Authentication Event Handlers

  /**
   * Handle signed in event from AWS Amplify Hub
   * Protected with processing flag to prevent concurrent execution
   */
  public session: any;
  public migratingPresonusUserAttributes: any;
  private async handleSignedInEvent() {
    // Set processing flag
    this.isProcessingAuthEvent = true;
    this.logAuthState("Starting handleSignedInEvent");

    try {
      let session = await this.getManagedAuthSession(false, "handleSignedInEvent");
      this.session = session;
      if (!session.tokens?.idToken) {
        console.error("No tokens in session after sign in");
        this.logAuthState("handleSignedInEvent failed - no tokens");
        return;
      }

      let prsId: any = session.tokens.idToken?.payload["custom:paeid"];
      // Check if this is a PreSonus user (has custom:paeid)
      let isPresonusToken = false;
      let prsAppClients = environment.cognito.appClients;
      for (var i in prsAppClients) {
        if (session.tokens.idToken.payload.aud == prsAppClients[i]) {
          isPresonusToken = true;
          break;
        }
      }
      if (isPresonusToken) {
        if (!environment.production) console.log("✅ PreSonus user found, migrating to Fender pool");
        this.migratingPresonusUserAttributes = session.tokens.idToken.payload;
        this.storageService.setItem(StorageKeys.MIGRATING_PRESONUS_USER_ID_TOKEN, session.tokens.idToken.toString());
        this.storageService.setItem(StorageKeys.MIGRATING_PRESONUS_USER_ATTRIBUTES, this.migratingPresonusUserAttributes);
        this.presonusIdToken = session.tokens.idToken.toString();

        if (this.letUserSignInWithPreSonus) {
          let refreshToken = (this.refreshToken = this.appService.getRefreshTokenFromLocalStorage(environment.fenderCognito.clientId, false));
          this.appService.clearAllCognitoStorage();
          this.appService.setLocalStorageTokens(session.tokens.accessToken.toString(), session.tokens.idToken.toString(), refreshToken);
          this.appService.syncCognitoTokensToCookies();
          window.location.href = window.location.origin + "/home";
          return;
        }

        // Release the processing flag BEFORE migration because migration will trigger a new signedIn event
        // that needs to be processed by a fresh handleSignedInEvent call
        this.isProcessingAuthEvent = false;
        this.logAuthState("Releasing processing flag before migration");

        await this.migratePreSonusUserToFender(session);

        // Early return - don't go to finally block since we already released the flag
        return;
      } else {
        // Regular Fender user, proceed with normal flow
        if (!environment.production) console.log("☑︎ Regular Fender or Presonus user found, proceeding with normal flow.");

        prsId = null;
        try {
          let payload = session.tokens.idToken.payload;
          payload.presonusIdToken = this.presonusIdToken || this.storageService.getItem(StorageKeys.MIGRATING_PRESONUS_USER_ID_TOKEN);
          // Send the presenting Fender id token explicitly (see processPreSonusAuthentication).
          payload.fenderIdToken = session.tokens.idToken.toString();
          if (!environment.production) console.log("⊕ Calling createUpdateFenderUser with payload:", payload);
          this.loading = true;
          let userDetails = await firstValueFrom(this.userService.createUpdateFenderUser(payload));
          if (!environment.production) console.log("🔄 createUpdateFenderUser returned userDetails:", userDetails);
          prsId = userDetails.id;
          if (!session.tokens.idToken.payload["custom:prs-id"]) {
            let attrs = { "custom:prs-id": prsId.toString() };
            attrs["given_name"] = userDetails.firstName || "first";
            attrs["family_name"] = userDetails.lastName || "last";
            if (!environment.production) console.log("✅ Updating user attributes with attributes:", attrs);
            await updateUserAttributes({ userAttributes: attrs });

            // Force a token refresh to get new tokens with updated attributes
            session = await this.getManagedAuthSession(true, "updateUserAttributes");
          }
          if (!environment.production) console.log("🎉 Done, bringing the user to the dashboard.");
          await this.setupSession(session, prsId);
        } catch (error) {
          let errMsg = this.helperService.retrieveErrorMessage(error);
          if (errMsg.includes("Must authenticate with PreSonus")) {
            this.handlePreSonusAuthenticationRequired(session);
          } else {
            console.error("Error in signedIn event handler:", error);
            this.handleSignInError(error);
          }
        }
      }
    } catch (error) {
      console.error("Error in signedIn event handler:", error);
      this.logAuthState("handleSignedInEvent error", { error: error?.message });
      this.handleSignInError(error);
    } finally {
      // Always clear processing flag
      this.isProcessingAuthEvent = false;
      this.logAuthState("Completed handleSignedInEvent");
    }
  }

  /**
   * Handle case where PreSonus authentication is required
   */
  private async handlePreSonusAuthenticationRequired(session: any): Promise<void> {
    if (!environment.production)
      console.log("💥 The user logged in with a Fender account and there exists a PreSonus account with the same email, so they must authenticate with their PreSonus account.");

    // lets first see if the same password works
    let presonusIdToken: any = false;
    try {
      let samePasswordResult: any = await firstValueFrom(this.userService.authenticatePresonusUser(this.email, this.password));
      if (samePasswordResult?.idToken) {
        presonusIdToken = samePasswordResult.idToken;
      }
    } catch (error) {
      this.loading = false;
    }

    try {
      if (!presonusIdToken) {
        this.authenticatingPresonusAccount = true;
        this.storageService.removeItem(StorageKeys.MIGRATING_PRESONUS_USER_ID_TOKEN);
        this.loading = false;
        let modalRef = this.appService.showModal(AuthenticatePresonusUserComponent, { size: "md" });
        modalRef.componentInstance.email = session.tokens.idToken.payload.email;
        presonusIdToken = await firstValueFrom(modalRef.componentInstance.idTokenRetrieved);
      }

      this.loading = true;
      await this.processPreSonusAuthentication(session, presonusIdToken as string);
    } catch (error) {
      console.error("Error in PreSonus authentication:", error);
      this.authenticatingPresonusAccount = false;
      this.handleSignInError(error);
    }
  }

  /**
   * Process PreSonus authentication and update user
   * Improved to avoid recursive sign-in loops
   */
  private async processPreSonusAuthentication(session: any, presonusToken: string): Promise<void> {
    let payload = {
      ...session.tokens.idToken.payload,
      presonusIdToken: presonusToken,
      // The presenting Fender id token, sent explicitly so paeapi can bind this Fender account.
      // Authenticating the PreSonus pool above flips the ambient id token, so the X-USER-TOKEN
      // header the interceptor attaches can be the PreSonus token by now — the body field survives it.
      fenderIdToken: session.tokens.idToken.toString(),
    };
    this.presonusIdToken = presonusToken;
    if (!environment.production) console.log("🔄 presonusToken retrieved:", presonusToken);
    if (!environment.production) console.log("⊕ Calling createUpdateFenderUser with payload:", payload);

    // Create/update user with both tokens
    this.loading = true;
    let userDetails = await firstValueFrom(this.userService.createUpdateFenderUser(payload));
    if (!environment.production) console.log("🔄 createUpdateFenderUser returned userDetails:", userDetails);

    let loggingInWithSocialProvider = this.storageService.getItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE);
    if (loggingInWithSocialProvider) {
      // Social provider flow - can safely fetch new session
      let refreshedSession = await this.getManagedAuthSession(false, "socialProviderFlow");
      await this.setupSession(refreshedSession, userDetails.id);
    } else {
      // Regular flow - instead of signing out and back in,
      // update the user attributes and refresh the session
      try {
        // Get fresh session with updated attributes
        let refreshedSession = await this.getManagedAuthSession(true, "processPreSonusAuth");
        await this.setupSession(refreshedSession, userDetails.id);
      } catch (error) {
        console.error("Failed to update user attributes:", error);
        // Fallback: If attribute update fails, show error instead of looping
        this.loginError = "Failed to complete authentication. Please try logging in again.";
        this.loading = false;
        await signOut();
        this.appService.deleteCookies();
      }
    }
  }

  // #endregion

  // #region Migration Methods

  /**
   * Migrate PreSonus user to Fender pool
   */
  private async migratePreSonusUserToFender(session: any) {
    this.logAuthState("Starting migratePreSonusUserToFender");

    try {
      // First sign out of PreSonus
      if (!environment.production) console.log("The user signed in with PreSonus, need to migrate to Fender.");
      await signOut();
      this.appService.deleteCookies();

      // Switch to Fender pool
      if (!environment.production) console.log("🔄 Switching to Fender pool.");
      this.switchPools("fender");

      // Wait a moment to ensure pool switch is complete
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create Fender account using the existing method
      // Note: This will trigger a new signedIn event that will be processed separately
      if (!environment.production) console.log("🔄 Creating Fender account (will trigger new signedIn event)");
      await this.createFenderAccount();

      this.logAuthState("Migration initiated - waiting for new signedIn event");
    } catch (error) {
      this.logAuthState("Migration error", { errorName: error?.name, errorMessage: error?.message });

      if (error?.name == "UsernameExistsException" || error?.name == "UserLambdaValidationException") {
        // User already has a Fender account, need to link it
        await this.handleExistingFenderAccount(error);
      } else {
        console.error("Failed to migrate user to Fender pool:", error);
        this.loading = false; // Ensure loading state is cleared
        this.handleSignInError(error);
      }
    }
  }

  /**
   * Handle case where Fender account already exists
   */
  private async handleExistingFenderAccount(error: any): Promise<void> {
    if (this.ssoType != "fender_shopify") {
      this.attemptPreSonusSignIn(true);
      return;
    } else {
      if (!environment.production)
        console.log("🔄 The user signed into the presonus account, but they already have a fender account. Need to sign them into their fender account in order to link the accounts.");
      this.loading = false;
      let modalRef = this.appService.showModal(AuthenticateFenderUserComponent, { size: "md" });
      modalRef.componentInstance.presonusIdToken = this.presonusIdToken;
      if (error.name == "UserLambdaValidationException") modalRef.componentInstance.isThirdPartyProvider = true;
      modalRef.componentInstance.email = this.email;

      const sessionData = await firstValueFrom(modalRef.componentInstance.fenderCognitoAttributesRetrieved);
      this.loading = true;
      if (!environment.production) console.log("🔄 User logged into their Fender account:", sessionData);

      let session = await this.getManagedAuthSession(false, "handleExistingFenderAccount");
      if (!environment.production) console.log("🔄 Setting up session with the user's Fender account.");
      this.setupSession(session, this.migratingPresonusUserAttributes["custom:paeid"]);
    }
  }

  /**
   * Create new Fender account for migrating PreSonus user
   */
  public createdNewFenderAccount: boolean = false;
  private async createFenderAccount() {
    const attrs = this.buildMigrationAttributes();
    if (!attrs["given_name"]) attrs["given_name"] = "first";
    if (!attrs["family_name"]) attrs["family_name"] = "last";
    this.createdNewFenderAccount = true;
    if (!environment.production) console.log("⊕ Creating Fender account with attrs:", attrs);
    try {
      const signUpResult = await signUp({
        username: this.email.toLowerCase(),
        password: this.password,
        options: {
          userAttributes: attrs,
          autoSignIn: true,
        },
      });
      if (!environment.production) console.log("Successfully created Fender account:", signUpResult);
    } catch (error) {
      this.createdNewFenderAccount = false;
      console.error("Error creating Fender account:", error);
      if (error?.name == "InvalidPasswordException") {
        // their old presonus password doesnt conform to fender.
        this.loading = false;
        const modalRef = this.appService.showModal(ChangePasswordComponent, { size: "md" });
        modalRef.componentInstance.creatingNewFenderPassword = true;
        modalRef.componentInstance.fenderPasswordSelected.subscribe(async (password) => {
          this.password = password;
          this.loading = true;
          this.appService.closeModals();
          await this.createFenderAccount();
        });
        return;
      } else if (error?.message?.indexOf("PreSignUp failed with error UserExists") > -1) {
        const err = new Error("User already exists");
        err.name = "UsernameExistsException";
        throw err;
      } else {
        throw error;
      }
    }

    // Wait for auto sign in to complete
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Try to sign in explicitly
    if (!environment.production) console.log("⊕ Signing in to Fender account.");
    await signIn({
      username: this.email.toLowerCase(),
      password: this.password,
      options: {
        authFlowType: "USER_SRP_AUTH",
      },
    });
  }

  /**
   * Build attributes for migrating user account
   */
  private buildMigrationAttributes(): any {
    let firstName = this.migratingPresonusUserAttributes["custom:first_name"] || this.migratingPresonusUserAttributes["given_name"] || "first";
    let lastName = this.migratingPresonusUserAttributes["custom:last_name"] || this.migratingPresonusUserAttributes["family_name"] || "last";
    return {
      email: this.email.toLowerCase(),
      ...{ "custom:source": this.source || "presonus" },
      // The PreSonus pool stores marketing consent as `custom:marketing_emails`; Fender's is
      // `custom:crm_opt_in`. Without this mapping a migrating user arrives with no opt-in at all,
      // is treated as opted out, and is skipped from Fender Iterable entirely (DENG-752/DENG-65).
      // It must be set here, at signUp, so the value is present when the account is created.
      "custom:crm_opt_in": this.migratingPresonusUserAttributes["custom:marketing_emails"] === "true" ? "true" : "false",
      ...(this.migratingPresonusUserAttributes && { "custom:prs-id": this.migratingPresonusUserAttributes["custom:paeid"] }),
      ...(firstName && { given_name: firstName }),
      ...(lastName && { family_name: lastName }),
      ...(this.migratingPresonusUserAttributes && { "custom:country_code": this.migratingPresonusUserAttributes["custom:country_code"] || this.selectedCountry?.code || "US" }),
    };
  }

  // #endregion

  // #region Session and Token Management

  /**
   * Handle signed out event
   */
  private async handleSignedOutEvent() {
    this.appService.deleteCookies();
    this.fbAuth.signOut().catch(() => {
      // Silently handle Firebase signOut errors - user is already logged out of Cognito
    });
    this.loggedInUser = undefined;
  }

  /**
   * Handle token refresh failure with retry limit to prevent infinite loops
   * This can happen during active login OR on page load with invalid/expired session
   */
  private async handleTokenRefreshFailureEvent() {
    this.logAuthState("Token refresh failure", {
      retryCount: this.tokenRefreshFailureCount,
      hasCredentials: !!(this.email && this.password),
      loadingCredentials: this.loadingCredentials,
    });

    // Guard: Check retry count to prevent infinite loops
    if (this.tokenRefreshFailureCount >= this.MAX_TOKEN_REFRESH_RETRIES) {
      if (!environment.production) console.log("❌ Max token refresh retries reached, forcing logout");
      this.tokenRefreshFailureCount = 0; // Reset for next session
      this.loading = false;
      this.loadingCredentials = false;
      await signOut();
      this.appService.deleteCookies();
      return;
    }

    this.tokenRefreshFailureCount++;
    if (!environment.production) console.log(`Token refresh failed (attempt ${this.tokenRefreshFailureCount}/${this.MAX_TOKEN_REFRESH_RETRIES})`);

    // Don't attempt to sign in again if already processing an auth event
    if (this.isProcessingAuthEvent) {
      if (!environment.production) console.log("⏸️ Already processing auth event, skipping re-authentication");
      return;
    }

    if (this.email && this.password) {
      // Scenario: Active login attempt, credentials available - try to re-authenticate
      if (!environment.production) console.log("🔄 Attempting re-authentication with stored credentials");
      try {
        this.isProcessingAuthEvent = true; // Set flag to prevent concurrent processing
        await signIn({
          username: this.email.toLowerCase(),
          password: this.password,
          options: {
            authFlowType: "USER_SRP_AUTH",
          },
        });
        // Success - reset counter
        this.tokenRefreshFailureCount = 0;
      } catch (error) {
        console.error("Failed to re-authenticate:", error);
        this.loading = false;

        // Only sign out if we've hit max retries
        if (this.tokenRefreshFailureCount >= this.MAX_TOKEN_REFRESH_RETRIES) {
          await signOut();
          this.appService.deleteCookies();
        }
      } finally {
        this.isProcessingAuthEvent = false;
      }
    } else {
      // Scenario: Page load/refresh with invalid/expired/missing tokens - just clean up silently
      // This is expected behavior when migrating storage (localStorage → cookies) or session expired
      if (!environment.production) console.log("🧹 No credentials available, cleaning up invalid session (expected on page load with expired/migrated tokens)");

      this.tokenRefreshFailureCount = 0; // Reset counter
      this.loading = false; // Clear any loading state
      this.loadingCredentials = false; // Clear credential loading state

      await signOut();
      this.appService.deleteCookies();

      // Don't show an error - this is expected when session expires or storage migrates
      // User will just see the login form
    }
  }

  // #endregion

  // #region User Session Management

  /**
   * Check for logged in user on component initialization
   */
  private async checkForLoggedInUser(cognitoClientId: string) {
    if (!environment.production) console.log("🔎 Checking for logged in user.");

    const session = await this.getManagedAuthSession(true, "checkForLoggedInUser");
    if (!session.tokens?.idToken) {
      this.loadingCredentials = false;
      if (this.appService.app == "sphere" && this.helperService.token && this.userService.user) {
        const updateArgs: any = {
          lastLoginTime: new Date(),
          id: this.loggedInUserId,
        };
        this.handleRegularSignIn(updateArgs);
      }
    } else {
      // If the user is already authenticated and simply refreshed /login, we still want to redirect.
      // We previously skipped processing when SOCIAL_SIGN_IN_FLOW_TYPE was present, but that flag can
      // be left behind from an earlier social sign-in click and will prevent redirect on refresh.
      const socialFlowType = this.storageService.getItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE);
      const qp = this.activatedRoute?.snapshot?.queryParams || {};
      const hasOauthCallbackParams = !!(qp.code || qp.state || qp.error || qp.error_description);

      if (socialFlowType && !hasOauthCallbackParams) {
        if (!environment.production) {
          console.log("🧹 Clearing stale SOCIAL_SIGN_IN_FLOW_TYPE on refresh:", socialFlowType);
        }
        this.storageService.removeItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE);
      }

      // If we're not currently in the OAuth callback step, process the session and redirect.
      // (During the actual callback step, Hub 'signedIn' will drive the post-login flow.)
      if (!this.storageService.getItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE) || !hasOauthCallbackParams) {
        await this.processExistingUserSession(session, cognitoClientId);
      }
    }
  }

  /**
   * Process existing user session
   */
  private async processExistingUserSession(session: any, cognitoClientId: string): Promise<void> {
    if (!environment.production) console.log("✅ Logged in user found!", session);

    this.idToken = session.tokens.idToken.toString();
    this.accessToken = session.tokens.accessToken.toString();

    // Use the actual client ID from the token to retrieve the refresh token
    // This prevents mismatches when using app-specific client IDs (e.g., STUDIO_ONE)
    const actualClientId = session.tokens.idToken.payload.aud;
    if (!environment.production) {
      console.log("🔑 Using actual client ID from token for refresh token retrieval:", actualClientId);
    }

    this.refreshToken = this.appService.getRefreshTokenFromLocalStorage(actualClientId);
    this.appService.setXplatformCookies();

    try {
      const user = await getCurrentUser();
      if (user) {
        this.cognitoUser = user;
        await this.handleExistingUser(session);
      }
      this.loadingCredentials = false;
    } catch (error) {
      this.loadingCredentials = false;
      await signOut();
      this.appService.deleteCookies();
    }
  }

  /**
   * Handle existing user authentication
   */
  private async handleExistingUser(session: any): Promise<void> {
    let attributes = await fetchUserAttributes();
    attributes = this.appService.processCognitoAttributes(attributes);

    if (attributes["custom:prs-id"]) {
      const userResult: any = await firstValueFrom(this.userService.getUserDetails(false, this.idToken));
      this.loadingCredentials = false;
      if (this.ssoType) {
        this.loggedInUser = userResult;
      } else {
        const updateArgs: any = {
          lastLoginTime: new Date(),
          id: this.loggedInUserId,
        };
        this.handleRegularSignIn(updateArgs);
      }
    } else {
      await this.handleUserWithoutPrsId(attributes, session);
    }
  }

  /**
   * Handle user without prs-id (migration case)
   */
  private async handleUserWithoutPrsId(attributes: any, session: any): Promise<void> {
    const migratingAttributes = this.storageService.getItem(StorageKeys.MIGRATING_PRESONUS_USER_ATTRIBUTES);

    if (migratingAttributes) {
      await this.processMigratingUser(attributes, migratingAttributes);
    } else {
      if (!environment.production) console.log("🔄 Signing the user out because there is no saved migrating presonus user attributes and prs-id is not set on the token.");
      await signOut();
      this.appService.deleteCookies();
    }
  }

  /**
   * Process migrating PreSonus user
   */
  private async processMigratingUser(attributes: any, migratingAttributes: any): Promise<void> {
    if (!environment.production) console.log("🔄 Migrating presonus user with a presonus token, from within checkForLoggedInUser with these attributes.", migratingAttributes);

    this.storageService.removeItem(StorageKeys.MIGRATING_PRESONUS_USER_ATTRIBUTES);
    const presonusToken = this.storageService.getItem(StorageKeys.MIGRATING_PRESONUS_USER_ID_TOKEN);
    const userDetails = await firstValueFrom(this.userService.createUpdateFenderUser({ ...attributes, presonusIdToken: presonusToken }));

    const attrs = this.buildMigrationUserAttributes(migratingAttributes);
    if (!environment.production) console.log("🔄 Updating fender user attributes with the following data", attrs);
    await updateUserAttributes({ userAttributes: attrs });

    // Force a token refresh to get new tokens with updated attributes
    const session = await this.getManagedAuthSession(true, "processMigratingUser");
    await this.setupSession(session, userDetails.id);
  }

  /**
   * Build user attributes for migration
   */
  private buildMigrationUserAttributes(migratingAttributes: any): any {
    const attrs: any = { "custom:prs-id": migratingAttributes["custom:paeid"].toString() };

    attrs["given_name"] = migratingAttributes["custom:first_name"] || "first";
    attrs["family_name"] = migratingAttributes["custom:last_name"] || "last";
    attrs["custom:country_code"] = migratingAttributes["custom:country_code"] || "US";
    attrs["custom:ip"] = migratingAttributes["custom:ip"] || "0.0.0.0";
    if (migratingAttributes["custom:source"]) attrs["custom:source"] = migratingAttributes["custom:source"] || "presonus";
    // Read from the PreSonus attribute name — a PreSonus token never carries `custom:crm_opt_in`,
    // so keying on that made this line dead code.
    if (migratingAttributes["custom:marketing_emails"]) attrs["custom:crm_opt_in"] = migratingAttributes["custom:marketing_emails"] === "true" ? "true" : "false";
    if (migratingAttributes["custom:terms"]) attrs["custom:terms"] = migratingAttributes["custom:terms"];
    if (migratingAttributes["custom:migrated"]) attrs["custom:migrated"] = migratingAttributes["custom:migrated"];

    return attrs;
  }

  /**
   * Show modal for PreSonus authentication
   */
  showPresonusAuthModal() {
    const modalRef = this.appService.showModal(AuthenticateFenderUserComponent, { size: "md" });
    modalRef.componentInstance.email = this.email;
    modalRef.componentInstance.presonusIdToken = this.presonusIdToken;
  }

  /**
   * Complete sign in process and redirect user
   */
  completeSignIn(updateArgs?: any) {
    if (!updateArgs) updateArgs = {};
    updateArgs.lastLoginTime = new Date();
    if (!updateArgs.id) updateArgs.id = this.loggedInUserId;

    if (this.cognitoUser?.username) updateArgs.cognito_user_id = this.cognitoUser.username;

    this.cleanupStorageItems();

    if (this.ssoType && this.ssoDestinations[this.ssoType]) {
      this.handleSSOSignIn(updateArgs);
    } else {
      this.handleRegularSignIn(updateArgs);
    }
  }

  /**
   * Clean up storage items after sign in
   */
  private cleanupStorageItems(): void {
    this.storageService.removeItem(StorageKeys.MIGRATING_PRESONUS_USER_ATTRIBUTES);
    this.storageService.removeItem(StorageKeys.MIGRATING_PRESONUS_USER_ID_TOKEN);
    this.storageService.removeItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE);
    // NOT SOCIAL_SIGN_IN_RETURN_TO: this runs before handleRegularSignIn -> determineRedirectUrl,
    // which is what reads it. Clearing it here would wipe the destination a moment before it is
    // used. It clears itself on read instead, and expires on its own if the flow is abandoned.
  }

  /**
   * Handle SSO sign in flow
   */
  private handleSSOSignIn(updateArgs: any): void {
    this.userService.saveUser(updateArgs, this.idToken).subscribe((result) => {
      const go = () => {
        this.userService.getUserDetails(false, this.idToken).subscribe({
          next: (result) => {
            this.loggedInUser = result;
            this.loading = false;
            this.storageService.removeItem(StorageKeys.SSO_PARAMS);
            this.initSso();
          },
          error: (err) => {
            this.loading = false;
            this.handleSignOutOnError();
          },
        });
      };

      this.userService.validateFenderMultipass(result.user.email, result.user["fc-id"]).subscribe({
        next: (result) => {
          go();
        },
        error: (err) => {
          go();
        },
      });
    });
  }

  /**
   * Handle regular (non-SSO) sign in flow
   */
  private async handleRegularSignIn(updateArgs: any): Promise<void> {
    this.loading = true;
    try {
      const result = await firstValueFrom(this.userService.saveUser(updateArgs, this.idToken));

      if (this.selectedSignupInterestsArray.length) {
        let args = [];
        let string_ids = [];
        this.selectedSignupInterestsArray.forEach((item) => {
          let obj = {
            user_id: this.user.id,
            skill_id: item.id,
          };
          args.push(obj);
          string_ids.push(item.string_id);
        });

        await new Promise((resolve) => setTimeout(resolve, 3000));
        await firstValueFrom(this.communityService.addUserSkills(args, string_ids));
        if (this.wantsEmail) {
          let interestsMap = {};
          this.signupInterests.forEach((item) => {
            interestsMap[item.iterable_mapping] = false;
          });
          this.selectedSignupInterestsArray.forEach((item) => {
            interestsMap[item.iterable_mapping] = true;
          });
          await firstValueFrom(this.userService.saveIterableSettings({ interests: interestsMap }));
        }
      }

      // Validate multipass, but proceed regardless of success/failure
      try {
        await firstValueFrom(this.userService.validateFenderMultipass(result.user.email, result.user["fc-id"]));
      } catch (err) {
        // Ignore errors, proceed with redirect
      }

      // Execute redirect logic
      this.determineRedirectUrl();
      this.storageService.removeItem(StorageKeys.SSO_PARAMS);
      if (this.appService.app == "fender") {
        if (this.returnto && this.returnto != "/home") {
          let returnTo = this.returnto;
          if (this.appService.app == "fender" && this.createdNewFenderAccount) {
            returnTo = returnTo.indexOf("?") > -1 ? returnTo + "&showLinkedAccountMessage=true" : returnTo + "?showLinkedAccountMessage=true";
          }
          window.location.href = returnTo;
        } else {
          if (this.createdNewFenderAccount && this.appService.app == "fender") {
            this.router.navigate(["/home"], { queryParams: { showLinkedAccountMessage: true } });
          } else {
            this.router.navigate(["/home"]);
          }
        }
      } else {
        let baseUrl = environment.mypresonusUrl;
        if (this.appService.app == "fender") baseUrl = environment.fenderUrl;
        window.location.href = this.returnto.startsWith("http") ? this.returnto : baseUrl.substring(0, baseUrl.length - 1) + this.returnto;
      }
    } catch (error) {
      this.loading = false;
      console.error("Error in handleRegularSignIn:", error);
    }
  }

  /**
   * Determine redirect URL after sign in
   */
  private determineRedirectUrl(): void {
    if (this.displayedAuthSection === "Sign Up" && (this.router.routerState.snapshot.url === "/" || this.router.routerState.snapshot.url === "/login") && !this.ssoType) {
      this.setReturnto("/home");
    } else if (this.router.routerState.snapshot.url.indexOf("mustangmicro") > -1 && !this.ssoType) {
      this.setReturnto("/home?register=mustangmicro");
    } else if (this.productSplash) {
      this.setReturnto(`/home?register=${this.activatedRoute.snapshot.params["splash"]}`);
    } else if (this.queryParams.returnto) {
      this.setReturnto(this.queryParams.returnto === "/login" ? "/home" : this.queryParams.returnto);
      if (this.returnto.indexOf("redirectedFromPresonus") > -1) {
        this.setReturnto(this.returnto.replace("?redirectedFromPresonus=true", ""));
        this.setReturnto(this.returnto.replace("&redirectedFromPresonus=true", ""));
      }
    } else {
      // Social sign-in can't carry a destination in the URL, so it left one in storage.
      // Ranked below an explicit `?returnto=` and above deriving one from the current URL,
      // which at this point is always /login.
      const stashed = this._consumeSocialReturnTo();
      if (stashed) {
        this.setReturnto(stashed);
      } else if (this.shouldSetReturnToFromCurrentUrl()) {
        this.setReturnto(this.router.routerState.snapshot.url);
      }
    }
  }

  /**
   * Remembers where the member was before a social provider takes over the browser.
   *
   * Cognito's `redirectSignIn` is fixed when Amplify is configured (see `switchPools`), so
   * unlike the email path there is nowhere in the URL to put a destination — it always comes
   * back to /login. Storage survives the round trip; the sibling SOCIAL_SIGN_IN_FLOW_TYPE
   * write immediately above every caller relies on the same thing.
   *
   * Stamped with a time so an abandoned attempt can't bounce someone weeks later.
   */
  private _stashSocialReturnTo(): void {
    const destination = this.returnto;
    if (!destination || destination === "/home") return;
    this.storageService.setItem(StorageKeys.SOCIAL_SIGN_IN_RETURN_TO, { url: destination, ts: Date.now() });
  }

  /**
   * Reads the stashed destination back, exactly once.
   *
   * Removed before it is returned, so a failure downstream can't leave it to fire on an
   * unrelated sign-in later. Rejects anything that isn't a same-origin relative path —
   * `handleRegularSignIn` assigns this straight to `window.location.href`, and a
   * protocol-relative `//host` would navigate off-site.
   */
  private _consumeSocialReturnTo(): string {
    const stashed: any = this.storageService.getItem(StorageKeys.SOCIAL_SIGN_IN_RETURN_TO);
    if (!stashed) return "";
    this.storageService.removeItem(StorageKeys.SOCIAL_SIGN_IN_RETURN_TO);

    const url = `${stashed.url || ""}`;
    const age = Date.now() - Number(stashed.ts || 0);
    if (!url || age < 0 || age > SOCIAL_RETURN_TO_TTL_MS) return "";
    if (!url.startsWith("/") || url.startsWith("//")) return "";
    return url;
  }

  /**
   * Check if return URL should be set from current URL
   */
  private shouldSetReturnToFromCurrentUrl(): boolean {
    const url = this.router.routerState.snapshot.url;
    return (
      url.indexOf("code") === -1 &&
      url.indexOf("logout") === -1 &&
      url.indexOf("createaccount") === -1 &&
      url.indexOf("login") === -1 &&
      url.indexOf("sso") === -1 &&
      ((this.displayedAuthSection === "Sign in" && url !== "/login" && url !== "/") || this.displayedAuthSection === "Sign Up")
    );
  }

  /**
   * Handle sign out on error
   */
  private handleSignOutOnError(): void {
    signOut()
      .then(() => this.appService.deleteCookies())
      .catch(() => this.appService.deleteCookies());
  }

  // #endregion

  // #region Utility Methods

  /**
   * Extract URL parameters from a string
   */
  findURLParameters(inputString: string) {
    const pattern = /\b(\w+)=(\w+)\b/g;
    const parameters = {};

    let match;
    while ((match = pattern.exec(inputString)) !== null) {
      const key = match[1];
      const value = match[2];
      parameters[key] = value;
    }

    return parameters;
  }

  // #endregion

  // #region Public Interface Methods

  /**
   * Main sign up method - validates and processes user registration
   */
  signUp() {
    if (this.loading || !this.validate()) {
      this.loginError = "Please fill out all the required fields.";
      return;
    }

    this.loading = true;
    this.verifyCaptchaAndProceed();
  }

  /**
   * Verify captcha and proceed with email verification
   */
  private verifyCaptchaAndProceed(): void {
    let version = this.showV2Captcha ? "v2" : "v3";
    if (version === "v3") {
      this.recaptchaV3Service.execute("signup").subscribe((token) => {
        this.userService.verifyCaptcha({ captcha: token, version: version, action: "signup", scoreThreshold: 0.5 }).subscribe({
          next: (result) => this.verifyEmailAndSignUp(),
          error: (error) => this.handleCaptchaError(),
        });
      });
    } else {
      this.userService.verifyCaptcha({ captcha: this.captcha, version: version }).subscribe({
        next: (result) => this.verifyEmailAndSignUp(),
        error: (error) => this.handleCaptchaError(),
      });
    }
  }

  /**
   * Handle captcha verification error
   */
  private handleCaptchaError(): void {
    this.loading = false;
    this.loginError = "Please check the box to prove you are human.";
    this.showV2Captcha = true;
  }

  /**
   * Handle CAPTCHA resolution
   */
  resolved(args: any) {
    this.captcha = args;
  }

  /**
   * Refresh CAPTCHA component
   */
  refreshCaptcha() {
    this.showV2Captcha = false;
    this._win.setTimeout(() => {
      this.showV2Captcha = true;
    }, 10);
  }

  /**
   * Verify email and proceed with sign up
   */
  private verifyEmailAndSignUp(): void {
    this.userService.verifyEmail(this.email.toLowerCase()).subscribe({
      next: (result) => {
        this.emailPasses = true;
        this.performSignUp();
      },
      error: (error) => this.handleEmailVerificationError(),
    });
  }

  /**
   * Handle email verification error
   */
  private handleEmailVerificationError(): void {
    this.loading = false;
    this.emailPasses = false;
    this.loginError = "The email you entered is not valid.";
  }

  /**
   * Perform the actual sign up operation
   */
  private performSignUp(): void {
    // Defensive: sign-up must always use the correct pool.
    // A common path to an incorrect pool is:
    // - user is in Fender mode
    // - login attempts Fender, then falls back to PreSonus (switchPools("presonus"))
    // - user then clicks "Sign Up" -> Amplify is still configured to PreSonus
    this.switchPools("fender");

    this.source = this.determineRegistrationSource();
    const signUpAttributes = this.buildSignUpAttributes();

    signUp({
      username: this.email.toLowerCase(),
      password: this.password,
      options: {
        userAttributes: signUpAttributes,
        autoSignIn: true,
      },
    })
      .then((result) => {
        return autoSignIn();
      })
      .catch((err) => this.handleSignUpError(err));
  }

  /**
   * Determine the registration source for analytics
   */
  private determineRegistrationSource(): string {
    let source = this.reftagService.getRefTag() || this.returnToUrlParams.source;
    if (!source) source = this.activatedRoute.snapshot.queryParams["source"];
    if (!source && this.ssoType == "fender_shopify") source = "FC_Shop";
    if (!source) source = this.appService.app === "presonus_connect" ? "presonus" : "myfender";
    return decodeURIComponent(source).split(/[?&]/)[0].substring(0, 36);
  }

  /**
   * Build user attributes for sign up
   */
  private buildSignUpAttributes(): any {
    const attrs: any = {
      email: this.email.toLowerCase(),
      "custom:crm_opt_in": this.wantsEmail ? "true" : "false",
      "custom:country_code": this.selectedCountry.code,
      "custom:ip": this.locationResult?.ip || "0.0.0.0",
      family_name: this.last_name || "first",
      given_name: this.first_name || "last",
    };

    if (this.source) {
      attrs["custom:source"] = this.source;
    }

    return attrs;
  }

  /**
   * Handle sign up errors
   */
  private handleSignUpError(err: any): void {
    this.loading = false;
    if (this.showV2Captcha) this.refreshCaptcha();
    const errorMessage = this.helperService.retrieveErrorMessage(err);

    if (errorMessage.indexOf("account_create_failed") > -1) {
      this.loginError = this._translate.instant("login.create_account_failed");
    } else if (errorMessage === "An account with the given email already exists.") {
      this.loginError = this._translate.instant("login.create_account_failed");
    } else if (errorMessage.indexOf("PostConfirmation failed with error") > -1) {
      // Remove the error prefix and trailing period
      this.loginError = errorMessage.substring(34, errorMessage.length - 1);
    } else {
      this.loginError = errorMessage;
    }

    this.trackSignUpError(err);
  }

  /**
   * Track sign up error for analytics (only sent when Avo defines errorSignUp, e.g. Connect; skipped in My Fender)
   */
  private trackSignUpError(err: any): void {
    const te = this.avoService.trackEvent();
    if (te && typeof te.errorSignUp === "function") {
      te.errorSignUp({
        errorCode: err?.code,
        errorMessage: this.loginError,
        errorName: err?.name,
        errorSource: "Cognito",
        siteId: "presonus_connect",
        url: window.location.href,
      });
    }
  }

  /**
   * Main sign in method
   */
  async signIn(force?: boolean) {
    if ((!this.loading || force) && this.validate()) {
      try {
        this.loading = true;

        // Clear all Cognito storage before attempting sign in to prevent stale token issues
        this.appService.clearAllCognitoStorage();

        await this.attemptFenderSignIn();
      } catch (err: any) {
        this.handleSignInError(err);
      }
    }
  }

  /**
   * Attempt sign in with Fender Cognito pool
   */
  private async attemptFenderSignIn() {
    try {
      this.switchPools("fender");
      const result = await signIn({
        username: this.email.toLowerCase(),
        password: this.password,
        options: {
          authFlowType: "USER_SRP_AUTH",
        },
      });

      // Check if password reset is required
      if (result.nextStep?.signInStep === "RESET_PASSWORD") {
        const resetError: any = new Error("Password reset required for the user");
        resetError.name = "PasswordResetRequiredException";
        resetError.__type = "PasswordResetRequiredException";
        throw resetError;
      }

      // Admin-created users with a temp password land here — show the set-password form
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        this.requiresNewPassword = true;
        this.loading = false;
        return;
      }
    } catch (fenderError) {
      if (!environment.production) console.log(fenderError);
      // Handle UserAlreadyAuthenticatedException error
      if (fenderError.name === "UserAlreadyAuthenticatedException") {
        await this.handleUserAlreadyAuthenticatedError();
        return; // Exit after handling the error
      }

      if (this.isUserNotFoundError(fenderError)) {
        if (!environment.production) console.log("User not found in Fender pool, attempting PreSonus pool...");
        await this.attemptPreSonusSignIn();
      } else if (this.isInvalidCookieError(fenderError)) {
        if (this.checkCookiesEnabled()) {
          throw new Error("There was an error signing you in, please try removing all cookies, reload your page and try again.");
        } else {
          throw new Error("This site requires cookies to be enabled to sign in. Please enable cookies and try again.");
        }
      } else {
        throw fenderError;
      }
    }
  }

  async submitNewPassword() {
    if (!this.userService.passwordTester.test(this.newPassword)) {
      this.loginError = this._translate.instant("profile.password_min");
      return;
    }
    if (this.newPassword !== this.confirmNewPassword) {
      this.loginError = this._translate.instant("login.passwords_do_not_match");
      return;
    }
    this.loading = true;
    this.loginError = "";
    try {
      await confirmSignIn({ challengeResponse: this.newPassword });
      // Hub 'signedIn' event fires → handleSignedInEvent() takes over from here
    } catch (error: any) {
      console.error("Failed to set new password:", error);
      this.loginError = this.helperService.retrieveErrorMessage(error);
      this.loading = false;
    }
  }

  /**
   * Check if error indicates user not found
   */
  private isUserNotFoundError(error: any): boolean {
    return error?.name === "UserNotFoundException" || error?.name === "NotAuthorizedException";
  }

  /**
   * Check if error indicates UnexpectedSignInInterruptionException
   */
  private isInvalidCookieError(error: any): boolean {
    return error?.name === "UnexpectedSignInInterruptionException";
  }

  private checkCookiesEnabled(): boolean {
    // First check navigator.cookieEnabled
    if (!navigator.cookieEnabled) {
      return false;
    }

    // Verify by actually testing cookie functionality
    try {
      document.cookie = "cookietest=1; SameSite=Strict";
      const enabled = document.cookie.indexOf("cookietest=") !== -1;
      document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT; SameSite=Strict";
      return enabled;
    } catch (e) {
      return false;
    }
  }

  /**
   * Handle UserAlreadyAuthenticatedException error by signing out the user
   */
  private async handleUserAlreadyAuthenticatedError(): Promise<void> {
    try {
      if (!environment.production) {
        console.log("UserAlreadyAuthenticatedException detected, signing out user...");
      }

      // Sign out the current user
      await signOut();

      // Clear service caches and cookies
      this.clearServiceCaches();
      this.appService.deleteCookies();

      // Reset form fields
      this.resetLoginForm();

      if (!environment.production) {
        console.log("User successfully signed out due to UserAlreadyAuthenticatedException");
      }

      // Reload the page to ensure clean state
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (signOutError) {
      if (!environment.production) {
        console.error("Error during sign out for UserAlreadyAuthenticatedException:", signOutError);
      }
      // Even if signOut fails, still clear caches and cookies
      this.clearServiceCaches();
      this.appService.deleteCookies();

      // Reset form and reload page anyway
      this.resetLoginForm();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  /**
   * Reset login form fields and related state
   */
  private resetLoginForm(): void {
    this.email = "";
    this.password = "";
    this.password2 = "";
    this.loginError = "";
    this.loading = false;
    this.passwordPasses = false;
    this.emailPasses = false;
    this.needsVerification = false;
    this.displayedAuthSection = "Sign in";
  }

  /**
   * Attempt sign in with PreSonus Cognito pool
   */
  public letUserSignInWithPreSonus: boolean = false;
  private async attemptPreSonusSignIn(letUserSignInWithPreSonus: boolean = false) {
    this.switchPools("presonus");
    this.letUserSignInWithPreSonus = letUserSignInWithPreSonus;
    try {
      const result = await signIn({
        username: this.email.toLowerCase(),
        password: this.password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH",
        },
      });

      // Check if password reset is required
      if (result.nextStep?.signInStep === "RESET_PASSWORD") {
        const resetError: any = new Error("Password reset required for the user");
        resetError.name = "PasswordResetRequiredException";
        resetError.__type = "PasswordResetRequiredException";
        throw resetError;
      }
    } catch (error) {
      console.error("PreSonus sign in failed:", error);
      throw error;
    }
  }

  /**
   * Sign in with Google OAuth
   */
  signInWithGoogle() {
    // Clear all Cognito storage before attempting sign in to prevent stale token issues
    this.appService.clearAllCognitoStorage();
    // Ensure Amplify is configured with correct OAuth redirect URLs before initiating OAuth flow
    // This prevents InvalidOriginException when the pool configuration wasn't yet applied
    this.switchPools("fender");
    this.storageService.setItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE, "google");
    this._stashSocialReturnTo();
    signInWithRedirect({
      provider: "Google",
    });
  }

  /**
   * Sign in with Facebook OAuth
   */
  signInWithFacebook() {
    // Clear all Cognito storage before attempting sign in to prevent stale token issues
    this.appService.clearAllCognitoStorage();
    // Ensure Amplify is configured with correct OAuth redirect URLs before initiating OAuth flow
    // This prevents InvalidOriginException when the pool configuration wasn't yet applied
    this.switchPools("fender");
    this.storageService.setItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE, "facebook");
    this._stashSocialReturnTo();
    signInWithRedirect({
      provider: "Facebook",
    });
  }

  /**
   * Sign in with Apple OAuth
   */
  signInWithApple() {
    // Clear all Cognito storage before attempting sign in to prevent stale token issues
    this.appService.clearAllCognitoStorage();
    // Ensure Amplify is configured with correct OAuth redirect URLs before initiating OAuth flow
    // This prevents InvalidOriginException when the pool configuration wasn't yet applied
    this.switchPools("fender");
    this.storageService.setItem(StorageKeys.SOCIAL_SIGN_IN_FLOW_TYPE, "apple");
    this._stashSocialReturnTo();
    signInWithRedirect({
      provider: "Apple",
    });
  }

  /**
   * Switch between Cognito pools
   */
  private switchPools(brand: "fender" | "presonus") {
    let redirectUrl = environment.mypresonusUrl + "login";
    if (this.appService.app == "presonus_connect") {
      redirectUrl = environment.connectUrl + "home";
    } else if (this.appService.app == "fender") {
      redirectUrl = environment.fenderUrl + "login";
    }
    this.currentPool = brand === "fender" ? "primary" : "secondary";
    const cognitoClientId = this.determineCognitoClientId(brand);
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: cognitoClientId,
          userPoolId: brand === "fender" ? environment.fenderCognito.userPoolId : environment.cognito.userPoolId,
          loginWith: {
            oauth: {
              providers: ["Google", "Facebook", "Apple"],
              domain: brand === "fender" ? environment.fenderCognito.domain : environment.cognito.domain,
              scopes: ["email", "profile", "openid", "aws.cognito.signin.user.admin"],
              redirectSignIn: [redirectUrl],
              redirectSignOut: [redirectUrl],
              responseType: "code",
            },
          },
        },
      },
    });
  }

  /**
   * Start forgot password flow - opens modal with Fender sign-in help text when Fender-branded
   */
  startForgotPassword(resetPassword?: boolean) {
    this.switchPools("fender");
    const modalRef = this.appService.showModal(ForgotpasswordComponent, { size: "sm" });
    const isFender = this.appService.app === "fender" || this.ssoType === "fender";
    modalRef.componentInstance.switchToFenderPool = !!isFender;
    const email = this.email || this.queryParams?.email;
    if (email) {
      modalRef.componentInstance.resetPasswordWithEmail = String(email).toLowerCase().trim();
    }
  }

  /**
   * Verify email with confirmation code
   */
  verify() {
    this.loading = true;
    confirmUserAttribute({
      userAttributeKey: "email",
      confirmationCode: this.verificationCode,
    })
      .then((result) => {
        fetchUserAttributes().then((attributes) => {
          attributes = this.appService.processCognitoAttributes(attributes);
          if (!attributes["custom:prs-id"] && !attributes["custom:paeid"]) {
            alert("No user ID found.");
          } else {
            const userId = attributes["custom:prs-id"] || attributes["custom:paeid"];
            this.completeSignIn({
              id: userId,
              active: true,
              source: this.source,
              wantsEmail: this.wantsEmail,
              updateIterable: true,
            });
          }
        });
      })
      .catch((err) => {
        this.loading = false;
        alert(err?.message || "An error occurred");
      });
  }

  /**
   * Resend verification code
   */
  resendCode() {
    sendUserAttributeVerificationCode({
      userAttributeKey: "email",
    });
    this.codeSent = true;
  }

  /**
   * Show country selector modal
   */
  showCountrySelector() {
    const modalRef = this.appService.showModal(SelectCountryComponent, { size: "sm" });
    const countries = [];
    for (const i in this.countriesMap) {
      countries.push({
        name: i,
        code: this.countriesMap[i],
      });
    }
    modalRef.componentInstance.countries = countries;
    modalRef.componentInstance.countrySelected.pipe(take(1)).subscribe((result) => {
      this.selectedCountry = result;
    });
  }

  /**
   * Skip straight to product splash screen
   */
  skipStraightToSplash() {
    const modalRef = this.appService.showModal(RegisterSuccessSplashComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.splash = this.productSplash;
  }

  /**
   * Navigate to MyPreSonus
   */
  goToMypresonus(typo?: boolean) {
    this.storageService.removeItem(StorageKeys.SSO_PARAMS);
    window.location.href = typo ? environment.mypresonusUrl + "/account/general" : environment.mypresonusUrl.substring(0, environment.mypresonusUrl.length - 1) + this.returnto;
  }

  /**
   * Logout user
   */
  logout() {
    try {
      if (this.avoService) {
        const trackEventResult = this.avoService.trackEvent();
        if (trackEventResult && typeof trackEventResult.loggedOut === "function") {
          trackEventResult.loggedOut({
            identifySource: "PreSonus Connect",
            siteId: "presonus_connect",
          });
        }
      }
    } catch (error) {
      // Silently fail analytics tracking to ensure logout continues
      if (!environment.production) {
        console.error("Failed to track logout event:", error);
      }
    }

    this.clearServiceCaches();
    this.needsVerification = false;
    this.userService.logout().subscribe((result) => {});

    signOut()
      .then(() => {
        this.handleLogoutSuccess();
      })
      .catch(() => {
        this.handleLogoutSuccess();
      });
  }

  /**
   * Clear service caches on logout
   */
  private clearServiceCaches(): void {
    this.productService.clearCache();
    this.eventsService.clearCache();
    this.orderService.clearCache();
    this.supportService.clearCache();
    this.communityService.clearCache();
    this.workspaceService.clearCache();
  }

  /**
   * Handle successful logout
   * Properly handles Firebase signOut promise to prevent unhandled rejections
   * that would otherwise propagate undefined to Angular's error handler
   */
  private handleLogoutSuccess(): void {
    this.appService.deleteCookies();
    this.fbAuth.signOut().catch(() => {
      // Silently handle Firebase signOut errors - user is already logged out of Cognito
      // Firebase may not be initialized or user may not be signed in
    });
    this.loggedInUser = undefined;
  }

  /**
   * Redirect to Zendesk
   */
  zendeskRedirect() {
    window.location.href = "https://presonus.zendesk.com/agent";
  }

  // #endregion

  // #region Form Validation and Interaction Methods

  /**
   * Validate form inputs
   */
  validate() {
    let passed = true;
    if (this.displayedAuthSection === "Sign in" && (!this.email || !this.password)) {
      this.loginError = this._translate.instant("login.fill_required_fields");
      passed = false;
    } else if (this.displayedAuthSection === "Sign Up" && (!this.email || !this.password || !this.first_name || !this.last_name)) {
      this.loginError = this._translate.instant("login.fill_required_fields");
      passed = false;
    } else if (this.displayedAuthSection === "Sign Up" && !this.passwordPasses) {
      // Password does not meet complexity/length requirements
      this.loginError = this._translate.instant("login.create_account_failed");
      passed = false;
    } else if (this.displayedAuthSection === "Sign Up" && !this.termsCheck) {
      this.loginError = this._translate.instant("terms.plz_agree_to_terms");
      passed = false;
    }
    return passed;
  }

  public emailEnteredOneTime: boolean = false;
  public emailFocused: boolean = false;
  public passwordTouched: boolean = false;
  public passwordFocused: boolean = false;

  /**
   * Handle email field blur event
   */
  emailBlur() {
    this.emailFocused = false;
    this.makeEmailLowerCase();
    if (this.email) this.emailEnteredOneTime = true;
  }

  /**
   * Handle email field focus event
   */
  emailFocus() {
    this.emailFocused = true;
  }

  /**
   * Check password strength
   */
  checkPassword() {
    this.passwordPasses = this.userService.passwordTester.test(this.password);
    return this.passwordPasses;
  }

  /**
   * Check email validity and employee status
   */
  checkEmail() {
    this.emailPasses = this.userService.emailTester.test(this.email.toLowerCase());
    this.isEmployeeEmail = this.email.toLowerCase().indexOf("@fender.com") > -1 || this.email.toLowerCase().indexOf("@presonus.com") > -1;

    if (this.ssoType === "zendesk" && this.isEmployeeEmail) {
      this.displayedAuthSection = "Sign in";
      this.authSections.forEach((item) => (item.selected = item.name === "Sign in"));
    }
    return this.emailPasses;
  }

  /**
   * Generate test email for development
   */
  generateEmail() {
    this.first_name = "Josh";
    this.last_name = "Allen";
    const email = <any>this.env.developer_email;
    const parts = email.split("@");
    const timestamp =
      (new Date().getMonth() + 1).toString() + new Date().getDate().toString() + "-" + new Date().getHours().toString() + new Date().getMinutes().toString() + new Date().getSeconds().toString();
    this.email = parts[0] + "+" + timestamp + "@" + parts[1];
    this.password = "Password123!";
    this.signUp();
  }

  /**
   * Handle password field blur event
   */
  passwordBlur() {
    this.passwordFocused = false;
    this.passwordTouched = true;
    this.checkPassword();
  }

  /**
   * Handle password field focus event
   */
  passwordFocus() {
    this.passwordFocused = true;
  }

  /**
   * Handle key up events
   */
  onKeyUp(args: any) {
    this.loginError = undefined;
    this.checkPassword();
    this.checkEmail();
  }

  /**
   * Convert email to lowercase
   */
  makeEmailLowerCase() {
    if (this.email) this.email = this.email.toLowerCase();
  }

  /**
   * Handle key down events
   */
  onKeyDown(args: any) {}

  /**
   * Initialize SSO flow
   */
  initSso() {
    this.shopifyStoreID = this.queryParams.storeID || "US";
    if (this.ssoType && this.ssoDestinations[this.ssoType].cognitoTokens) {
      this.handleCognitoTokensSSO();
    } else {
      this.handleStandardSSO();
    }
  }

  /**
   * Handle SSO with Cognito tokens
   */
  private handleCognitoTokensSSO(): void {
    if (this.ssoType === "mobile") {
      if ((<any>window).FenderAuth && (<any>window).FenderAuth.onAuthSuccess) {
        (<any>window).FenderAuth.onAuthSuccess({
          accessToken: this.idToken,
          refreshToken: this.refreshToken,
          expiresIn: 3600, // seconds until access token expires
          userId: this.loggedInUser.id,
          userEmail: this.loggedInUser.email, // helpful for native apps
        });
      }
      return;
    } else {
      let redirectTo = this.ssoDestinations[this.ssoType].src + `?refresh_token=${this.refreshToken}`;
      if (this.queryParams.redirect_uri) {
        redirectTo = this.queryParams.redirect_uri + `?refresh_token=${this.refreshToken}`;
      }
      window.location.href = redirectTo;
    }
  }

  /**
   * Handle standard SSO flow
   */
  private handleStandardSSO(): void {
    this._spin.show();
    const args = this.buildSSOArguments();

    this.userService.sso(args, this.accessToken).subscribe({
      next: (result: any) => {
        if (result && result.token) {
          this.processSSOResult(result, args);
        }
      },
    });
  }

  /**
   * Build SSO arguments
   */
  private buildSSOArguments(): any {
    const args: any = {
      type: this.ssoType,
      firstName: this.loggedInUser.firstName,
      lastName: this.loggedInUser.lastName,
      photoURL: this.loggedInUser.photoURL,
      createdTime: this.loggedInUser.createdTime,
      username: this.loggedInUser.username,
      user_id: this.loggedInUser.id,
      user_agent: window.navigator.userAgent,
      storeID: this.shopifyStoreID,
    };

    if (this.ssoDestinations[this.ssoType].returnQueryParam) {
      let returnToUrl = this.queryParams[this.ssoDestinations[this.ssoType].returnQueryParam];
      if (returnToUrl) args.return_to = returnToUrl;
    }

    args.includeGroups = this.ssoType && this.ssoDestinations[this.ssoType]?.extraParams?.groups ? true : false;
    args.groupDescriptions = this.ssoType && this.ssoDestinations[this.ssoType]?.extraParams?.groupDescriptions ? true : false;
    args.includeProductCount = this.ssoType && this.ssoDestinations[this.ssoType]?.extraParams?.products ? true : false;
    args.includeForumPermissions = this.ssoType && this.ssoDestinations[this.ssoType]?.extraParams?.permissions ? true : false;

    return args;
  }

  /**
   * Process SSO result and redirect
   */
  private processSSOResult(result: any, args: any): void {
    if (this.ssoType.indexOf("shopify") > -1) {
      this.handleShopifySSO(result, args);
    } else {
      this.handleGenericSSO(result);
    }
  }

  /**
   * Handle Shopify-specific SSO
   */
  private handleShopifySSO(result: any, args: any): void {
    let redirectURL = "";
    const parsedReturnUrl = new URL(args.return_to);
    const baseDomain = `${parsedReturnUrl.protocol}//${parsedReturnUrl.host}`;
    redirectURL = baseDomain + "/account/login/multipass/" + result.token;
    window.location.href = redirectURL;
  }

  /**
   * Handle generic SSO redirect
   */
  private handleGenericSSO(result: any): void {
    if (this.ssoDestinations[this.ssoType].returnQueryParam && this.queryParams[this.ssoDestinations[this.ssoType].returnQueryParam]) {
      window.location.href = this.queryParams[this.ssoDestinations[this.ssoType].returnQueryParam];
    } else if (this.ssoDestinations[this.ssoType].src) {
      this.handleSSORedirect(result);
    }
  }

  /**
   * Handle SSO redirect based on type
   */
  private handleSSORedirect(result: any): void {
    if (this.ssoType === "uux") {
      this.handleUUXSSO(result);
    } else if (this.ssoType === "admin") {
      window.location.href = environment.adminUrl.substring(0, environment.adminUrl.length - 1) + this.returnto;
    } else {
      this.handleReferrerBasedSSO(result);
    }
  }

  /**
   * Handle UUX-specific SSO
   */
  private handleUUXSSO(result: any): void {
    let returnToUrl = this.queryParams["return_to"] || this.queryParams["returnto"];
    if (!returnToUrl) returnToUrl = document.referrer;

    window.location.href =
      this.ssoDestinations[this.ssoType].src + (this.ssoDestinations[this.ssoType].type === "queryParam" ? result.token : "") + (returnToUrl ? "&return_to=" + encodeURIComponent(returnToUrl) : "");
  }

  /**
   * Handle referrer-based SSO
   */
  private handleReferrerBasedSSO(result: any): void {
    const referrer = document.referrer;
    if (referrer && this.ssoDestinations[this.ssoType].useReferrer) {
      if (this.ssoDestinations[this.ssoType].type === "queryParam") {
        const url = new URL(referrer);
        const queryParamName = this.ssoDestinations[this.ssoType].queryParamName;
        url.searchParams.append(queryParamName, result.token);
        window.location.href = url.toString();
      } else {
        window.location.href = referrer;
      }
    } else {
      window.location.href = this.ssoDestinations[this.ssoType].src + (this.ssoDestinations[this.ssoType].type === "queryParam" ? result.token : "");
    }
  }

  // #endregion

  // #region Core Authentication Methods (Moved from earlier sections)

  /**
   * Setup session after successful authentication
   * Protected against recursive sign-in loops
   */
  private async setupSession(session: any, prsId?: any) {
    // Guard: Prevent concurrent session setup
    if (this.sessionSetupInProgress) {
      if (!environment.production) console.log("⏸️ Session setup already in progress, skipping");
      this.logAuthState("setupSession skipped - already in progress");
      return;
    }

    // Guard: Validate session has required tokens
    // Fixes Bugsnag error 698134d29cb22f058bf5f84a: "Cannot read properties of undefined (reading 'idToken')"
    if (!session?.tokens?.idToken || !session?.tokens?.accessToken) {
      console.error("Invalid session: missing tokens", { hasSession: !!session, hasTokens: !!session?.tokens });
      this.logAuthState("setupSession failed - invalid session", {
        hasSession: !!session,
        hasTokens: !!session?.tokens,
        hasIdToken: !!session?.tokens?.idToken,
        hasAccessToken: !!session?.tokens?.accessToken,
      });
      this.loginError = this._translate.instant("login.session_invalid_error");
      this.loading = false;
      await signOut();
      this.appService.deleteCookies();
      return;
    }

    this.sessionSetupInProgress = true;
    this.logAuthState("Starting setupSession", { prsId });

    try {
      const idToken = session.tokens.idToken.toString();
      const accessToken = session.tokens.accessToken.toString();

      // Get the actual client ID from the token's audience claim (aud)
      // This ensures we retrieve the refresh token with the correct client ID
      const actualClientId = session.tokens.idToken.payload.aud;
      if (!environment.production) {
        console.log("🔑 Using actual client ID from token:", actualClientId);
      }

      const refreshToken = this.appService.getRefreshTokenFromLocalStorage(actualClientId);

      let savedPresonusId = session.tokens.idToken.payload["custom:prs-id"];

      if (!savedPresonusId) {
        // User ID is missing from token - need to get user details, update attributes, and refresh session
        // This is a defensive fallback that shouldn't normally happen
        if (!environment.production) console.log("⚠️ User ID missing from token, fetching user details and updating attributes");
        this.logAuthState("Missing user ID in token - attempting recovery", {
          hasPresonusId: !!session.tokens.idToken.payload["custom:paeid"],
          hasFenderId: !!session.tokens.idToken.payload["custom:prs-id"],
        });

        try {
          // Get user details which will save/retrieve the user ID from backend
          const userResult: any = await firstValueFrom(this.userService.getUserDetails(false, idToken));

          if (!userResult || !userResult.id) {
            throw new Error("User details returned but no ID found");
          }

          // Update Cognito attributes with the correct user ID
          const attrKey = "custom:prs-id";
          if (!environment.production) console.log(`⚠️ Updating ${attrKey} attribute with value:`, userResult.id);

          await updateUserAttributes({
            userAttributes: {
              [attrKey]: userResult.id.toString(),
            },
          });

          // Force refresh session to get new tokens with updated attributes
          if (!environment.production) console.log("⚠️ Forcing session refresh to get updated token");
          const refreshedSession = await this.getManagedAuthSession(true, "setupSession-missingUserId");

          // Recursively call setupSession with the refreshed session
          // The sessionSetupInProgress flag is still true, so release it first
          this.sessionSetupInProgress = false;
          await this.setupSession(refreshedSession, userResult.id);
          return;
        } catch (error) {
          console.error("Failed to recover from missing user ID:", error);
          this.logAuthState("Failed to recover from missing user ID", { error: error?.message });
          this.loginError = "Failed to complete login. Please try again.";
          this.loading = false;
          // Sign out to clean state
          await signOut();
          this.appService.deleteCookies();
          return;
        }
      }

      this.accessToken = accessToken;
      this.idToken = idToken;
      this.refreshToken = refreshToken;
      this.appService.setXplatformCookies();

      const attributes = await fetchUserAttributes();
      this.handleUserAttributes(attributes, prsId);
      this.logAuthState("Completed setupSession successfully");
    } finally {
      this.sessionSetupInProgress = false;
    }
  }

  /**
   * Handle user attributes after authentication
   */
  private handleUserAttributes(attributes: any, prsId?: any) {
    attributes = this.appService.processCognitoAttributes(attributes);

    this.trackUserLogin(attributes, prsId);
    this.setupUserSession(attributes, prsId);
    this.handleEmailVerificationFlow(attributes);
  }

  /**
   * Track user login for analytics
   */
  private trackUserLogin(attributes: any, prsId?: any): void {
    const userId = prsId || attributes["custom:prs-id"] || attributes["custom:paeid"];
    const te = this.avoService.trackEvent();

    if (te && typeof te.loggedIn === "function") {
      te.loggedIn({
        identifySource: "PreSonus Connect",
        prsId: userId,
        siteId: "presonus_connect",
      });
    }

    if (this.displayedAuthSection === "Sign Up" && te && typeof te.accountCreated === "function") {
      te.accountCreated({
        identifySource: "PreSonus Connect",
        siteId: "presonus_connect",
        prsId: userId,
        userId: userId,
        locale: attributes["custom:country_code"],
      });
    }
  }

  /**
   * Setup user session data
   */
  private setupUserSession(attributes: any, prsId?: any): void {
    this.loggedInUserId = prsId || attributes["custom:prs-id"] || attributes["custom:paeid"];
    this.reftagService.deleteReftagCookie();
  }

  /**
   * Handle email verification flow for new users
   */
  private handleEmailVerificationFlow(attributes: any): void {
    const isSocialProvider = this.checkIfSocialProvider(attributes);

    if (!attributes.email_verified && !isSocialProvider) {
      this.loading = false;
      sendUserAttributeVerificationCode({
        userAttributeKey: "email",
      });
      this.needsVerification = true;
    } else {
      this.completeSignIn();
    }
  }

  /**
   * Check if user signed in with social provider
   */
  private checkIfSocialProvider(attributes: any): boolean {
    if (attributes.identities) {
      const identities = JSON.parse(attributes.identities);
      return identities.some((identity: any) => identity.providerName === "Google" || identity.providerName === "Facebook" || identity.providerName === "SignInWithApple");
    }
    return false;
  }

  /**
   * Handle sign in errors
   */
  private handleSignInError(err: any) {
    this.loading = false;
    const errorMessage = this.helperService.retrieveErrorMessage(err);

    if (errorMessage.indexOf("Exists only in SFCC") > -1) {
      this.handleSfccError(errorMessage);
    } else {
      this.processStandardSignInError(err, errorMessage);
    }
  }

  /**
   * Process standard sign in errors
   */
  private processStandardSignInError(err: any, errorMessage: string): void {
    const incorrectCredentialsMsg = this._translate.instant("login.incorrect_credentials");
    const isFenderConnectNotFound =
      errorMessage?.indexOf("can't find a Fender Connect account") > -1 || (errorMessage?.indexOf("can't find") > -1 && errorMessage?.indexOf("Google, Apple, or Facebook") > -1);

    if (
      err?.code === "UserNotFoundException" ||
      err?.name === "UserNotFoundException" ||
      err?.name === "NotAuthorizedException" ||
      err?.message?.indexOf("User does not exist") > -1 ||
      isFenderConnectNotFound
    ) {
      this.loginError = incorrectCredentialsMsg;
    } else if (err?.message === "Password reset required for the user") {
      this.startForgotPassword(true);
    } else {
      this.loginError = errorMessage;
    }

    this.trackSignInError(err);
  }

  /**
   * Track sign in error for analytics (only sent when Avo defines errorSignIn, e.g. Connect; skipped in My Fender)
   */
  private trackSignInError(err: any): void {
    const te = this.avoService.trackEvent();
    if (te && typeof te.errorSignIn === "function") {
      te.errorSignIn({
        errorCode: err?.code,
        errorMessage: this.loginError,
        errorName: err?.name,
        errorSource: "Cognito",
        siteId: "presonus_connect",
        url: window.location.href,
      });
    }
  }

  /**
   * Handle SFCC-specific errors
   */
  private handleSfccError(errorMessage: string) {
    const nameData = this.extractNameData(errorMessage);
    if (nameData) {
      this.first_name = nameData.firstName;
      this.last_name = nameData.lastName;
    }
    this.loading = false;
    this.signUp();
  }

  /**
   * Extract name data from error message
   */
  private extractNameData(inputString: string) {
    const match = inputString.match(/data=(\{.*?\})\./);
    if (match && match[1]) {
      try {
        return JSON.parse(match[1]);
      } catch (error) {
        console.error("Failed to parse JSON:", error);
        return null;
      }
    }
    return null;
  }

  // #endregion
}
